/**
 * @file Toast.tsx
 * @description Toast notification system with context provider
 *
 * @changelog
 * - 2026-02-15: Added action buttons (P3), dedup identical toasts (P4)
 * - 2026-02-13: Fixed timer leak — track timeouts in ref, clear on dismiss/unmount
 * - 2026-02-03: Added dark mode support
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  type ReactNode,
} from 'react';

type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  action?: ToastAction;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, action?: ToastAction) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

// --- Imperative escape hatch ---
// showApiError (services/errorPresentation/showApiError.ts) must be callable from
// plain catch blocks — and, in future, from utilities outside any component — so
// it can't rely on useToast()'s context lookup. The mounted ToastProvider
// registers its showToast here; dispatchToast is a no-op (dev-only console
// warning) if called before the provider mounts.
let toastDispatch: ToastContextType['showToast'] | null = null;

export function dispatchToast(message: string, type: ToastType = 'info', action?: ToastAction) {
  if (toastDispatch) {
    toastDispatch(message, type, action);
  } else if (import.meta.env.DEV) {
    console.warn('dispatchToast called before ToastProvider mounted:', message);
  }
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timeoutRefs = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const activeMessagesRef = useRef<Set<string>>(new Set());

  const removeToast = useCallback((id: string, dedupKey?: string) => {
    const timer = timeoutRefs.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timeoutRefs.current.delete(id);
    }
    if (dedupKey) {
      activeMessagesRef.current.delete(dedupKey);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, type: ToastType = 'info', action?: ToastAction) => {
      // P4: Dedup — skip if identical toast is already showing
      const dedupKey = `${type}:${message}`;
      if (activeMessagesRef.current.has(dedupKey)) return;
      activeMessagesRef.current.add(dedupKey);

      const id = Math.random().toString(36).substring(7);
      setToasts((prev) => [...prev, { id, message, type, action }]);
      const timer = setTimeout(() => {
        timeoutRefs.current.delete(id);
        activeMessagesRef.current.delete(dedupKey);
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 5000);
      timeoutRefs.current.set(id, timer);
    },
    []
  );

  // Cleanup all pending timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutRefs.current.forEach((timer) => clearTimeout(timer));
      timeoutRefs.current.clear();
      activeMessagesRef.current.clear();
    };
  }, []);

  // Register this provider's showToast as the imperative dispatch target (see
  // dispatchToast above). Only one ToastProvider is ever mounted (App.tsx root).
  useEffect(() => {
    toastDispatch = showToast;
    return () => {
      if (toastDispatch === showToast) toastDispatch = null;
    };
  }, [showToast]);

  const contextValue = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div className="fixed right-4 bottom-4 z-50 flex flex-col gap-2">
        {toasts.map((toast) => (
          <ToastItem
            key={toast.id}
            toast={toast}
            onRemove={() => removeToast(toast.id, `${toast.type}:${toast.message}`)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: () => void }) {
  const typeClasses = {
    success: 'bg-green-50 dark:bg-green-900/50 border-green-500 text-green-800 dark:text-green-200',
    error: 'bg-red-50 dark:bg-red-900/50 border-red-500 text-red-800 dark:text-red-200',
    info: 'bg-blue-50 dark:bg-blue-900/50 border-blue-500 text-blue-800 dark:text-blue-200',
    warning:
      'bg-yellow-50 dark:bg-yellow-900/50 border-yellow-500 text-yellow-800 dark:text-yellow-200',
  };

  const actionClasses = {
    success: 'text-green-700 hover:text-green-900 dark:text-green-300 dark:hover:text-green-100',
    error: 'text-red-700 hover:text-red-900 dark:text-red-300 dark:hover:text-red-100',
    info: 'text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-100',
    warning:
      'text-yellow-700 hover:text-yellow-900 dark:text-yellow-300 dark:hover:text-yellow-100',
  };

  const iconPaths = {
    success: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    error: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z',
    info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    warning:
      'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  };

  const handleAction = () => {
    if (!toast.action) return;
    try {
      toast.action.onClick();
    } catch (err) {
      console.error('Toast action handler failed:', err);
    }
    onRemove();
  };

  return (
    <div
      className={`animate-slide-in flex max-w-sm items-center gap-3 rounded-lg border-l-4 px-4 py-3 shadow-lg dark:shadow-gray-900/50 ${typeClasses[toast.type]}`}
      role="alert"
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
    >
      <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d={iconPaths[toast.type]}
        />
      </svg>
      <p className="flex-1 text-sm font-medium">{toast.message}</p>
      {toast.action && (
        <button
          type="button"
          onClick={handleAction}
          className={`text-sm font-medium underline transition-colors ${actionClasses[toast.type]}`}
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={onRemove}
        aria-label="Dismiss notification"
        className="text-current opacity-70 transition-opacity hover:opacity-100"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );
}
